import { LocationPicker } from "../LocationPicker";
import { BarterForSelect } from "../BarterForSelect";
import { CategorySelect } from "../CategorySelect";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { MapPin } from "lucide-react";

export const TitleSection = ({ value, onChange, error }) => {
  return (
    <div className="space-y-2">
      <Label htmlFor="title">
        Title <span className="text-red-500">*</span>
      </Label>
      <Input
        id="title"
        placeholder="Fresh Tomatoes from my Garden"
        value={value}
        onChange={onChange}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const DescriptionSection = ({ value, onChange, error }) => {
  return (
    <div className="space-y-2">
      <Label htmlFor="description">
        Description <span className="text-red-500">*</span>
      </Label>
      <Textarea
        id="description"
        placeholder="Describe your homegrown food, when it was harvested, and what you might be looking to trade for..."
        value={value}
        onChange={onChange}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const CategorySection = ({ value, onChange, error }) => {
  return (
    <div className="space-y-2">
      <Label htmlFor="category">
        Category <span className="text-red-500">*</span>
      </Label>
      <CategorySelect value={value} onChange={onChange} error={error} />
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

/**
 * Location section for listings.
 *
 * UX: defaults to "use my profile location" — most users will want this.
 * If they want a different location for this specific listing, they can
 * toggle "Use a different location for this listing" to reveal the picker.
 *
 * If they have no profile location, we don't show the toggle — the picker
 * is required.
 *
 * Props:
 *   profileLocation: { coordinates, label } | null  — user's saved location
 *   value:           same shape — what's currently selected for this listing
 *   onChange:        called when the user picks/clears a custom location
 *   useProfile:      bool — is the listing using the profile location?
 *   onToggleProfile: called when user toggles between profile / custom
 *   error:           validation message
 */
export const ListingLocationSection = ({
  profileLocation,
  value,
  onChange,
  useProfile,
  onToggleProfile,
  error,
}) => {
  const hasProfileLocation = !!(
    profileLocation && profileLocation.coordinates
  );

  // No profile location at all → user must set one or pick a custom one
  if (!hasProfileLocation) {
    return (
      <div className="space-y-2">
        <Label>
          Location <span className="text-red-500">*</span>
        </Label>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-2">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-900 mb-1">
                You don't have a saved location.
              </p>
              <p className="text-amber-800">
                Add a location to your profile so this and future listings can
                inherit it, or pick a one-off location below.{" "}
                <Link
                  to="/edit-profile"
                  className="font-medium text-amber-900 underline"
                >
                  Edit profile
                </Link>
              </p>
            </div>
          </div>
        </div>
        <LocationPicker
          value={value}
          onChange={onChange}
          countryCode="ZA"
          error={error}
        />
      </div>
    );
  }

  // User has a profile location — default UX is "use it", with override toggle
  return (
    <div className="space-y-2">
      <Label>
        Location <span className="text-red-500">*</span>
      </Label>

      {useProfile ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-3 py-2.5 bg-sage-50 border border-sage-200 rounded-lg">
            <MapPin className="w-4 h-4 text-sage-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-sage-700">From your profile</div>
              <div className="text-sm text-sage-900 truncate">
                {profileLocation.label || "Saved location"}
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="link"
            onClick={() => onToggleProfile(false)}
            className="p-0 h-auto text-xs text-igbanwo-mint"
          >
            Use a different location for this listing
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <LocationPicker
            value={value}
            onChange={onChange}
            countryCode="ZA"
            error={error}
            placeholder="Search for this listing's location..."
          />
          <Button
            type="button"
            variant="link"
            onClick={() => onToggleProfile(true)}
            className="p-0 h-auto text-xs text-igbanwo-mint"
          >
            ← Use my profile location instead
          </Button>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export const BarterForSection = ({ value, onChange, error }) => {
  return (
    <div className="space-y-2">
      <Label htmlFor="barterFor">Barter For</Label>
      <BarterForSelect value={value} onChange={onChange} error={error} />
    </div>
  );
};